import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { requestChangeHref } from '../../utils/request-change';
import {
  Alert,
  Button,
  Card,
  DataGrid,
  Dialog,
  EmptyState,
  Input,
  Select,
  TableScroll,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-jurisdiction-rules';
import { Calculator as CalcIcon, MapPin } from 'lucide-react';
import {
  useJurisdictions,
  useJurisdictionFeeSchedule,
  useCalculateFee,
} from '../../hooks/useUccJurisdictions';
import { formatDate, formatMoney, NOT_RECORDED } from './format';
import { ListHeader } from '../collateral/CollateralListParts';

type Jurisdiction = components['schemas']['Jurisdiction'];
type SubmissionMethod = components['schemas']['SubmissionMethod'];
type JurisdictionFilingType = components['schemas']['FilingType'];

/** Labels per SubmissionMethod; keyed on the generated union so tsc flags a new value. */
export const SUBMISSION_METHOD_LABELS: Record<SubmissionMethod, string> = {
  electronic: 'Electronic',
  paper: 'Paper',
  both: 'Electronic and paper',
};

/**
 * Labels per jurisdiction-rules FilingType. This contract spells filing types
 * UCC1 / UCC3_AMENDMENT; the filing-lifecycle contract spells them ucc1 /
 * ucc3Amendment. The two are separate enums and are not interchangeable.
 */
export const FILING_TYPE_LABELS: Record<JurisdictionFilingType, string> = {
  UCC1: 'UCC-1',
  UCC3_AMENDMENT: 'UCC-3 Amendment',
  UCC3_CONTINUATION: 'UCC-3 Continuation',
  UCC3_TERMINATION: 'UCC-3 Termination',
  UCC3_ASSIGNMENT: 'UCC-3 Assignment',
};

const FILING_TYPES = Object.keys(FILING_TYPE_LABELS) as JurisdictionFilingType[];

/** A jurisdiction's name for its row controls, falling back to its code. */
export function jurisdictionName(j: Jurisdiction): string {
  return j.stateName || j.stateCode;
}

export function submissionMethodsLabel(methods: readonly SubmissionMethod[] | undefined): string {
  if (!methods || methods.length === 0) return NOT_RECORDED;
  return methods.map((m) => SUBMISSION_METHOD_LABELS[m] ?? m).join(', ');
}

// ---------------------------------------------------------------------------
// Expanded row: the jurisdiction's fee schedule, read when the row opens
// ---------------------------------------------------------------------------

function FeeSchedulePanel({ jurisdiction }: { jurisdiction: Jurisdiction }) {
  const { data: schedule, isLoading, isError, error } = useJurisdictionFeeSchedule(
    jurisdiction.stateCode,
  );

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      <div className="md:col-span-2">
        <p className="text-xs font-sans font-semibold uppercase tracking-wider text-tertiary mb-2">
          Fee schedule
        </p>
        {isLoading ? (
          <p className="text-sm font-sans text-tertiary">Loading fee schedule...</p>
        ) : isError ? (
          <p className="text-sm font-sans text-error">
            The fee schedule could not be read: {(error as Error).message}
          </p>
        ) : !schedule || schedule.entries.length === 0 ? (
          <p className="text-sm font-sans text-tertiary">
            No fee schedule is configured for {jurisdiction.stateName}.
          </p>
        ) : (
          <>
            <TableScroll label="Fee schedule">
              <table className="w-full min-w-[480px] text-sm font-sans">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-tertiary">
                    <th scope="col" className="py-1 pr-4">Filing type</th>
                    <th scope="col" className="py-1 pr-4 text-right">Base fee</th>
                    <th scope="col" className="py-1 pr-4 text-right">Pages included</th>
                    <th scope="col" className="py-1 pr-4 text-right">Each added page</th>
                    <th scope="col" className="py-1 pr-4 text-right">Each added debtor</th>
                    <th scope="col" className="py-1 text-right">Expedited</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.entries.map((entry) => (
                    <tr key={entry.id} className="border-t border-recessed">
                      <td className="py-1.5 pr-4 text-primary">
                        {FILING_TYPE_LABELS[entry.filingType] ?? entry.filingType}
                      </td>
                      <td className="py-1.5 pr-4 text-right font-mono tabular-nums text-primary">
                        {formatMoney(entry.baseFee)}
                      </td>
                      <td className="py-1.5 pr-4 text-right font-mono tabular-nums text-secondary">
                        {entry.standardPageAllotment}
                      </td>
                      <td className="py-1.5 pr-4 text-right font-mono tabular-nums text-secondary">
                        {formatMoney(entry.additionalPageFee)}
                      </td>
                      <td className="py-1.5 pr-4 text-right font-mono tabular-nums text-secondary">
                        {formatMoney(entry.additionalDebtorFee)}
                      </td>
                      <td className="py-1.5 text-right font-mono tabular-nums text-secondary">
                        {entry.expeditedAvailable && entry.expeditedFee != null
                          ? formatMoney(entry.expeditedFee)
                          : entry.expeditedAvailable
                            ? 'Offered, fee not recorded'
                            : 'Not offered'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <p className="mt-2 text-xs font-sans text-tertiary">
              Effective {formatDate(schedule.effectiveDate)}. Source: {schedule.sourceStatute}.
            </p>
          </>
        )}
      </div>
      <div>
        <p className="text-xs font-sans font-semibold uppercase tracking-wider text-tertiary mb-2">
          Filing office
        </p>
        <p className="text-sm font-sans text-primary">{jurisdiction.filingOfficeName || NOT_RECORDED}</p>
        {jurisdiction.electronicFilingPortalUrl && (
          <a
            href={jurisdiction.electronicFilingPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-sans text-accent hover:underline"
          >
            Electronic filing portal
          </a>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const EMPTY_CALC_FORM = {
  stateCode: '',
  filingType: 'UCC1' as JurisdictionFilingType,
  pageCount: '1',
  additionalDebtorCount: '0',
};

function wholeNumber(value: string, min: number): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const n = Number(value);
  return n >= min ? n : null;
}

export function JurisdictionReports() {
  const { data, isLoading, isError, error, refetch, isFetching } = useJurisdictions();
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [calcForm, setCalcForm] = useState(EMPTY_CALC_FORM);
  const calculateFee = useCalculateFee();

  const jurisdictionList: Jurisdiction[] = data?.data ?? [];

  const pageCount = wholeNumber(calcForm.pageCount, 1);
  const additionalDebtorCount = wholeNumber(calcForm.additionalDebtorCount, 0);
  const calcReady = !!calcForm.stateCode && pageCount != null && additionalDebtorCount != null;

  const updateCalc = (patch: Partial<typeof EMPTY_CALC_FORM>) => {
    // A shown result belongs to the inputs that produced it; any edit clears it.
    calculateFee.reset();
    setCalcForm((prev) => ({ ...prev, ...patch }));
  };

  const handleCalculate = async () => {
    if (!calcReady) return;
    try {
      await calculateFee.mutateAsync({
        stateCode: calcForm.stateCode,
        body: {
          filingType: calcForm.filingType,
          pageCount: pageCount!,
          additionalDebtorCount: additionalDebtorCount!,
        },
      });
    } catch {
      // Error visible via mutation state
    }
  };

  const columns: ColumnDef<Jurisdiction, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'stateCode',
        header: 'State',
        cell: ({ row }) => (
          <span className="text-sm font-mono font-medium text-primary">{row.original.stateCode}</span>
        ),
        size: 80,
      },
      {
        accessorKey: 'stateName',
        header: 'Name',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-primary">{row.original.stateName || NOT_RECORDED}</span>
        ),
      },
      {
        id: 'submissionMethods',
        header: 'Submission methods',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">
            {submissionMethodsLabel(row.original.submissionMethods)}
          </span>
        ),
      },
      {
        accessorKey: 'hasElectronicFiling',
        header: 'Electronic filing',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">
            {row.original.hasElectronicFiling ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        accessorKey: 'filingOfficeName',
        header: 'Filing office',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">
            {row.original.filingOfficeName || NOT_RECORDED}
          </span>
        ),
      },
    ],
    [],
  );

  const result = calculateFee.data;

  return (
    <div>
      <ListHeader
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            {/* The page shows rules and fee schedules and no compliance field,
                so the subtitle no longer promises compliance data (UI run r3,
                fi-g5-8). */}
            Read-only view of UCC filing jurisdiction rules and fee schedules
            configured at the platform level. Expand a state to read its fee
            schedule.{' '}
            {/* GH #1962: was "Contact Aaim platform support to add or modify a
                jurisdiction." -- a dead end. Now files a tracked in-app request. */}
            <Link
              to={requestChangeHref({
                surface: 'UCC jurisdiction reports',
                item: 'Add or modify a jurisdiction rule',
                detail:
                  'Jurisdiction rules and fee schedules are configured at the platform level',
              })}
              className="text-accent hover:underline"
            >
              Request a jurisdiction change
            </Link>
          </p>
        }
      >
        <Button variant="primary" onClick={() => setCalcDialogOpen(true)}>
          <CalcIcon size={16} aria-hidden="true" />
          Fee Calculator
        </Button>
      </ListHeader>

      <DataGrid<Jurisdiction>
        data={jurisdictionList}
        columns={columns}
        isLoading={isLoading}
        errorMessage={isError ? `Jurisdictions could not be read: ${(error as Error).message}` : undefined}
        emptyState={
          <EmptyState
            icon={<MapPin className="h-8 w-8" />}
            title="No jurisdictions are configured"
            description="Jurisdiction rules are configured at the platform level. Request a jurisdiction change to add one."
          />
        }
        enableExpanding
        renderSubComponent={({ row }) => <FeeSchedulePanel jurisdiction={row.original} />}
        // The expander is the only way to read a fee schedule here, so it
        // names the state it opens: 'Show fee schedule for Alaska', not
        // 'Expand row' (UI run r3, fi-g5-7).
        getRowLabel={jurisdictionName}
        rowExpandLabel={(row) => `Show fee schedule for ${jurisdictionName(row)}`}
        getRowId={(row) => row.stateCode}
        hoverable
      />

      <Dialog
        open={calcDialogOpen}
        onOpenChange={(open) => {
          if (!open) setCalcDialogOpen(false);
        }}
        title="Fee Calculator"
        size="sm"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setCalcDialogOpen(false)}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleCalculate}
              disabled={calculateFee.isPending || !calcReady}
            >
              {calculateFee.isPending ? 'Calculating...' : 'Calculate'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            id="fee-calc-state"
            label="State"
            value={calcForm.stateCode}
            onChange={(e) => updateCalc({ stateCode: e.target.value })}
          >
            <option value="" disabled>
              Select a state
            </option>
            {jurisdictionList.map((j) => (
              <option key={j.stateCode} value={j.stateCode}>
                {j.stateCode} - {j.stateName}
              </option>
            ))}
          </Select>
          <Select
            id="fee-calc-filing-type"
            label="Filing type"
            value={calcForm.filingType}
            onChange={(e) => updateCalc({ filingType: e.target.value as JurisdictionFilingType })}
          >
            {FILING_TYPES.map((t) => (
              <option key={t} value={t}>
                {FILING_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
          <Input
            id="fee-calc-pages"
            label="Pages"
            type="number"
            min={1}
            step={1}
            value={calcForm.pageCount}
            onChange={(e) => updateCalc({ pageCount: e.target.value })}
            error={pageCount == null ? 'Enter a whole number of pages, 1 or more.' : undefined}
          />
          <Input
            id="fee-calc-debtors"
            label="Additional debtors"
            type="number"
            min={0}
            step={1}
            value={calcForm.additionalDebtorCount}
            onChange={(e) => updateCalc({ additionalDebtorCount: e.target.value })}
            helperText="Debtors beyond the first named on the filing."
            error={
              additionalDebtorCount == null ? 'Enter a whole number, 0 or more.' : undefined
            }
          />
          {result && (
            <Card className="p-4 bg-elevated">
              <p className="text-xs font-sans font-semibold uppercase tracking-wider text-tertiary">
                Calculated fee
              </p>
              <p className="text-2xl font-serif font-semibold text-primary tabular-nums">
                {formatMoney(result.totalFee, result.currency || 'USD')}
              </p>
              {result.breakdown.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm font-sans">
                  {result.breakdown.map((item) => (
                    <li key={item.component} className="flex justify-between gap-4">
                      <span className="text-secondary">{item.component}</span>
                      <span className="font-mono tabular-nums text-primary">
                        {formatMoney(item.amount, result.currency || 'USD')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
          {calculateFee.error && (
            <Alert type="error">{(calculateFee.error as Error).message}</Alert>
          )}
        </div>
      </Dialog>
    </div>
  );
}
